import { StandardEvents } from '@shopify/events';

class MixAndMatchBundle extends HTMLElement {
  #controller = new AbortController();
  #offer = 'single';
  #nativeButtonText = '';
  #nativeButtonDisabled = false;
  #bundlePriceMinor;
  #loading = false;
  #acceleratedCheckoutWasHidden = false;
  #stickyAddToCartWasHidden = false;

  connectedCallback() {
    const bundlePriceSource = this.dataset.bundlePriceMinor;
    this.#bundlePriceMinor = Number.parseInt(bundlePriceSource || '', 10);
    if (!Number.isFinite(this.#bundlePriceMinor)) {
      console.warn('[mix-and-match-bundle] Invalid bundle price in minor units; bundle offer disabled.', {
        value: bundlePriceSource,
      });
      this.hidden = true;
      return;
    }

    this.#acceleratedCheckoutWasHidden = this.#acceleratedCheckout?.hidden || false;
    this.#stickyAddToCartWasHidden = this.#stickyAddToCart?.hidden || false;

    const { signal } = this.#controller;
    this.addEventListener('change', this.#handleOfferChange, { signal });
    this.#secondaryPicker?.addEventListener('change', this.#handleSecondaryChange, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    this.#selectSecondaryVariant(this.dataset.activeVariantId || '');
    this.#captureNativeButtonState();
    this.#updatePrices(this.dataset.activeVariantPrice);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #purchaseArea() {
    return this.closest('.product-details--purchase');
  }

  get #secondaryPicker() {
    return this.#purchaseArea?.querySelector('[data-bundle-secondary]');
  }

  get #productForm() {
    return this.#purchaseArea?.querySelector('product-form-component');
  }

  get #addButton() {
    return this.#productForm?.querySelector('[ref="addToCartButton"]');
  }

  get #buttonText() {
    return this.#addButton?.querySelector('.add-to-cart-text__content > span > span');
  }

  get #acceleratedCheckout() {
    return this.#productForm?.querySelector('.accelerated-checkout-block');
  }

  get #stickyAddToCart() {
    return document.querySelector(`sticky-add-to-cart[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`);
  }

  #handleOfferChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== `everthen-offer-${this.dataset.productId}`) {
      return;
    }

    const nextOffer = event.target.value === 'bundle' ? 'bundle' : 'single';
    if (nextOffer === 'bundle' && this.#offer === 'single') this.#captureNativeButtonState();
    this.#offer = nextOffer;
    this.#syncOfferState();
  };

  #handleSecondaryChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.disabled) return;
    this.dataset.secondaryVariantId = event.target.dataset.variantId || '';
    this.#updateSecondaryColorName(event.target);
  };

  isBundleActive() {
    return this.#offer === 'bundle';
  }

  getBundleItems() {
    const primaryVariantId = this.#getPrimaryVariantId();
    const secondaryInput = this.#secondaryPicker?.querySelector('input:checked:not(:disabled)');
    const secondaryVariantId = secondaryInput instanceof HTMLInputElement ? secondaryInput.dataset.variantId : '';

    if (!this.#isBundleValid(primaryVariantId, secondaryVariantId)) return [];

    const quantitiesByVariant = new Map();
    for (const variantId of [primaryVariantId, secondaryVariantId]) {
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + 1);
    }
    return Array.from(quantitiesByVariant, ([id, quantity]) => ({ id, quantity }));
  }

  beginBundleSubmission(items) {
    if (this.#loading || !Array.isArray(items) || items.length === 0) {
      this.#updateBundleButtonState();
      return false;
    }

    this.#loading = true;
    this.#setSelectionControlsDisabled(true);
    this.#updateBundleButtonState();
    return true;
  }

  handleBundleResult(error) {
    if (error) {
      console.warn('[mix-and-match-bundle] Bundle add failed.', error);
    }
    this.#loading = false;
    this.#setSelectionControlsDisabled(false);
    this.#syncOfferState();
  }

  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const variant = detail?.resource;
        if (String(detail?.productId) !== String(this.dataset.productId)) return;

        if (!variant) {
          this.dataset.activeVariantId = '';
          this.dataset.activeVariantAvailable = 'false';
          requestAnimationFrame(() => {
            this.#captureNativeButtonState(true);
            this.#syncOfferState();
          });
          return;
        }

        this.dataset.activeVariantId = String(variant.id);
        this.dataset.activeVariantPrice = String(variant.price);
        this.dataset.activeVariantAvailable = String(variant.available === true);
        this.#updatePrices(variant.price);
        this.#selectSecondaryVariant(String(variant.id));

        requestAnimationFrame(() => {
          this.#captureNativeButtonState(true);
          this.#syncOfferState();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mix-and-match-bundle] Variant update failed:', error);
      });
  };

  #syncOfferState() {
    const isBundle = this.#offer === 'bundle';
    const secondaryPicker = this.#secondaryPicker;
    const quantity = this.#productForm?.querySelector('[ref="quantitySelectorWrapper"], .quantity-selector-wrapper');
    const acceleratedCheckout = this.#acceleratedCheckout;
    const stickyAddToCart = this.#stickyAddToCart;

    if (secondaryPicker) secondaryPicker.hidden = !isBundle;
    if (quantity) {
      quantity.hidden = isBundle;
      quantity.toggleAttribute('data-bundle-quantity-hidden', isBundle);
      if (isBundle) {
        quantity.setAttribute('aria-hidden', 'true');
      } else {
        quantity.removeAttribute('aria-hidden');
      }
    }

    if (acceleratedCheckout) acceleratedCheckout.hidden = isBundle || this.#acceleratedCheckoutWasHidden;
    if (stickyAddToCart) {
      stickyAddToCart.hidden = isBundle || this.#stickyAddToCartWasHidden;
      stickyAddToCart.toggleAttribute('data-bundle-hidden', isBundle);
    }

    if (isBundle) {
      this.#updateBundleButtonState();
    } else {
      this.#restoreNativeButtonState();
    }

    this.#updateNativeColorLabel();
  }

  #captureNativeButtonState(force = false) {
    const button = this.#addButton;
    const text = this.#buttonText;
    if (!button || !text) return;

    if (this.#offer === 'single' || force) {
      this.#nativeButtonText = text.textContent?.trim() || '';
      this.#nativeButtonDisabled = button.disabled;
    }
  }

  #updateBundleButtonState() {
    const button = this.#addButton;
    const text = this.#buttonText;
    if (!button || !text) return;

    text.textContent = this.#loading ? this.dataset.bundleLoadingLabel : this.#bundleIdleText;
    button.disabled = this.#loading || !this.#isBundleValid();
    button.toggleAttribute('aria-busy', this.#loading);
  }

  #restoreNativeButtonState() {
    const button = this.#addButton;
    const text = this.#buttonText;
    if (!button || !text) return;
    text.textContent = this.#nativeButtonText;
    button.disabled = this.#nativeButtonDisabled;
    button.removeAttribute('aria-busy');
  }

  get #bundleIdleText() {
    return `${this.dataset.bundleAddLabel} — ${this.#formatMoney(this.#bundlePriceMinor)}`;
  }

  #getPrimaryVariantId() {
    return this.#productForm?.querySelector('input[name="id"]')?.value || this.dataset.activeVariantId || '';
  }

  #isBundleValid(primaryVariantId = this.#getPrimaryVariantId(), secondaryVariantId = this.dataset.secondaryVariantId) {
    if (this.dataset.activeVariantAvailable !== 'true') return false;
    if (!/^\d+$/.test(primaryVariantId || '') || !/^\d+$/.test(secondaryVariantId || '')) return false;

    const secondaryInput = this.#secondaryPicker?.querySelector(
      `input[data-variant-id="${CSS.escape(secondaryVariantId)}"]`
    );
    return secondaryInput instanceof HTMLInputElement && !secondaryInput.disabled;
  }

  #setSelectionControlsDisabled(disabled) {
    const controls = this.#purchaseArea?.querySelectorAll(
      '.everthen-bundle-offer__radio, variant-picker input, [data-bundle-secondary] input'
    );
    if (!controls) return;

    for (const control of controls) {
      if (!(control instanceof HTMLInputElement)) continue;
      if (disabled && !control.disabled) {
        control.disabled = true;
        control.dataset.bundleTemporarilyDisabled = 'true';
      } else if (!disabled && control.dataset.bundleTemporarilyDisabled === 'true') {
        control.disabled = false;
        delete control.dataset.bundleTemporarilyDisabled;
      }
    }
  }

  #updateNativeColorLabel() {
    const label = this.#purchaseArea?.querySelector('variant-picker [data-everthen-color-label]');
    if (!label) return;
    label.textContent = this.#offer === 'bundle' ? 'Camera 1 color' : label.dataset.originalLabel || '';
  }

  #selectSecondaryVariant(preferredVariantId) {
    const picker = this.#secondaryPicker;
    if (!picker) return;

    const preferred = picker.querySelector(`input[data-variant-id="${CSS.escape(preferredVariantId)}"]:not(:disabled)`);
    const selected = picker.querySelector('input:checked:not(:disabled)');
    const fallback = picker.querySelector('input:not(:disabled)');
    const input = selected || preferred || fallback;

    if (input instanceof HTMLInputElement) {
      input.checked = true;
      this.dataset.secondaryVariantId = input.dataset.variantId || '';
      this.#updateSecondaryColorName(input);
    }
  }

  #updateSecondaryColorName(input) {
    const selectedName = this.#secondaryPicker?.querySelector('[data-secondary-color-name]');
    if (selectedName) selectedName.textContent = input.dataset.colorName || '';
  }

  #formatMoney(minorUnits) {
    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: this.dataset.currencyCode,
    });

    return formatter.format(minorUnits / 100);
  }

  #updatePrices(activeVariantPriceSource) {
    const activeVariantPriceMinor = Number.parseInt(String(activeVariantPriceSource ?? ''), 10);
    const bundlePriceMinor = Number.parseInt(String(this.dataset.bundlePriceMinor ?? ''), 10);
    const savingText = this.querySelector('[data-saving]');

    if (!Number.isFinite(activeVariantPriceMinor) || !Number.isFinite(bundlePriceMinor)) {
      console.warn('[mix-and-match-bundle] Price update skipped because a minor-unit value is invalid.', {
        activeVariantPrice: activeVariantPriceSource,
        bundlePrice: this.dataset.bundlePriceMinor,
      });
      if (savingText instanceof HTMLElement) savingText.hidden = true;
      return;
    }

    const regularTotalMinor = activeVariantPriceMinor * 2;
    const savingMinor = regularTotalMinor - bundlePriceMinor;

    if (!Number.isFinite(regularTotalMinor) || !Number.isFinite(savingMinor)) {
      console.warn('[mix-and-match-bundle] Calculated minor-unit price is invalid.', {
        regularTotalMinor,
        savingMinor,
      });
      if (savingText instanceof HTMLElement) savingText.hidden = true;
      return;
    }

    const singlePrice = this.querySelector('[data-single-price]');
    const bundlePrice = this.querySelector('[data-bundle-price]');
    const regularPrice = this.querySelector('[data-regular-total]');

    if (singlePrice) singlePrice.textContent = this.#formatMoney(activeVariantPriceMinor);
    if (bundlePrice) bundlePrice.textContent = this.#formatMoney(bundlePriceMinor);
    if (regularPrice) regularPrice.textContent = this.#formatMoney(regularTotalMinor);
    if (savingText) {
      savingText.hidden = false;
      savingText.textContent = `Save ${this.#formatMoney(savingMinor)}`;
    }
  }
}

if (!customElements.get('mix-and-match-bundle')) {
  customElements.define('mix-and-match-bundle', MixAndMatchBundle);
}
